// ** React Imports
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'

// ** Third Party Components
import classnames from 'classnames'
import { ArrowUp } from 'react-feather'

// ** Hooks
import { useSkin } from '@hooks/useSkin'

// ** Configs
import themeConfig from '@configs/themeConfig'

// ** NTTS nav
import Sidebar from './SideNav'
import { buildSections, findActive } from './navModel'

// ** Styles (tokens first, then nav, then app-specific overrides)
import '@src/assets/scss/ntts-tokens.css'
import '@src/assets/scss/ntts-nav.css'
import './ntts-shell.scss'

const NARROW_QUERY = '(max-width: 1100px)'
const COLLAPSED_KEY = 'ntts-nav-collapsed'

const readCollapsed = () => {
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === 'true'
  } catch (e) {
    return false
  }
}

const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches)
  useEffect(() => {
    const mql = window.matchMedia(query)
    const onChange = (e) => setMatches(e.matches)
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [query])
  return matches
}

const ScrollTopButton = () => {
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const onScroll = () => setVisible(window.pageYOffset >= 300)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])
  if (!visible) return null
  return (
    <button
      type="button"
      className="ntts-scroll-top"
      aria-label="Scroll to top"
      onClick={() => window.scroll({ top: 0, behavior: 'smooth' })}
    >
      <ArrowUp />
    </button>
  )
}

const NttsLayout = ({ menuData, children }) => {
  const location = useLocation()
  const { skin, setSkin } = useSkin()
  const menuHidden = useSelector((state) => state.layout.menuHidden)

  const isNarrow = useMediaQuery(NARROW_QUERY)
  const [collapsed, setCollapsed] = useState(readCollapsed)
  // Narrow screens: the sidebar is an icon strip that opens as an overlay
  const [panelOpen, setPanelOpen] = useState(false)

  const nav = useMemo(() => buildSections(menuData), [menuData])
  const active = useMemo(
    () => findActive(location.pathname, nav),
    [location.pathname, nav]
  )

  // ** Theme: tokens switch on <html data-theme>, synced with the app skin
  useEffect(() => {
    const theme = skin === 'dark' ? 'dark' : 'light'
    document.documentElement.dataset.theme = theme
    try {
      window.localStorage.setItem('ntts-theme', theme)
    } catch (e) {}
  }, [skin])

  useEffect(() => {
    try {
      window.localStorage.setItem(COLLAPSED_KEY, String(collapsed))
    } catch (e) {}
  }, [collapsed])

  // ** Close the overlay panel on navigation
  useEffect(() => {
    setPanelOpen(false)
  }, [location.pathname])

  // ** Esc closes the overlay panel
  useEffect(() => {
    if (!panelOpen) return
    const onKey = (e) => e.key === 'Escape' && setPanelOpen(false)
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [panelOpen])

  const handleToggle = () => {
    if (isNarrow) setPanelOpen((open) => !open)
    else setCollapsed((value) => !value)
  }

  // Used when a collapsed sidebar needs its labels (group click, Ctrl K)
  const handleExpand = useCallback(() => {
    if (isNarrow) setPanelOpen(true)
    else setCollapsed(false)
  }, [isNarrow])

  const handleNavigate = useCallback(() => setPanelOpen(false), [])

  const isCollapsed = isNarrow ? !panelOpen : collapsed

  return (
    <div
      className={classnames('app-shell', {
        'is-collapsed': isCollapsed,
        'is-panel-open': isNarrow && panelOpen,
        'is-menu-hidden': menuHidden
      })}
    >
      {!menuHidden && (
        <>
          <Sidebar
            nav={nav}
            active={active}
            collapsed={isCollapsed}
            onToggle={handleToggle}
            onExpand={handleExpand}
            onNavigate={handleNavigate}
            skin={skin}
            setSkin={setSkin}
          />
          {isNarrow && panelOpen && (
            <div
              className="ntts-panel-scrim"
              aria-hidden="true"
              onClick={() => setPanelOpen(false)}
            />
          )}
        </>
      )}

      <div className="app-main">
        {children}
      </div>

      {themeConfig.layout.scrollTop && <ScrollTopButton />}
    </div>
  )
}

export default NttsLayout
